/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  AppealListParams,
  fetchAppealForClerk,
  fetchAppealForCoordinatorWithType,
  fetchAppealForRegistrar,
  fetchAppealForRequestingProsecutor,
  fetchAppealRequest,
} from "services/appealService";
import {
  AppealRequest,
  AppealRequestStatus,
  AppealRequestType,
  Roles,
} from "types";
import { errorHandler, hasRole } from "utils/helper";
import { getStatusChipColor } from "components/AppealUIComponents";
import PageHeader from "../../components/PageHeader";

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const key = status as keyof typeof AppealRequestStatus;
  const display = AppealRequestStatus[key] || status;

  return (
    <span className={getStatusChipColor(display)}>
      {t(`status_${status.toLowerCase()}`, { defaultValue: display })}
    </span>
  );
};

const CassationAppealList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    t("all", { defaultValue: "All" }),
  );
  const [prosecutorAppeals, setProsecutorAppeals] = useState<AppealRequest[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const PAGE_SIZE = 20;

  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isVariousClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);

  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isVariousAppealCoordinator = hasRole(loginData?.roles, [
    Roles.variousAppealCoordinator,
  ]);

  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isEconomicAppealCoordinator = hasRole(loginData?.roles, [
    Roles.economicAppealCoordinator,
  ]);

  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : // Directors see the same cassations as their crime-type appeal coordinator.
      isVariousDirector
      ? Roles.variousAppealCoordinator
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isVariousAppealCoordinator
          ? Roles.variousAppealCoordinator
          : isEconomicCoordinator
            ? Roles.economicCoordinator
            : isEconomicDirector
              ? Roles.economicAppealCoordinator
              : isEconomicRegistrar
                ? Roles.economicRegistrar
                : isEconomicAppealCoordinator
                  ? Roles.economicAppealCoordinator
                  : isCorruptionCoordinator
                    ? Roles.corruptionCoordinator
                    : isCorruptionDirector
                      ? Roles.corruptionAppealCoordinator
                      : isCorruptionRegistrar
                        ? Roles.corruptionRegistrar
                        : isCorruptionAppealCoordinator
                          ? Roles.corruptionAppealCoordinator
                          : null;

  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  // Status filter tabs — All + the 4 key lifecycle stages.
  const STATUS_TAB_KEYS = [
    "PENDING",
    "ASSIGNED_TO_PROSECUTOR",
    "SUBMITTED_TO_COURT",
    "COMPLETED",
  ] as const;
  const STATUS_TABS: { label: string; status: string | null }[] = [
    { label: t("all", { defaultValue: "All" }), status: null },
    ...STATUS_TAB_KEYS.map((key) => ({
      label: t(`appeal_request_status_${key.toLowerCase()}`, {
        defaultValue: AppealRequestStatus[key],
      }),
      status: key,
    })),
  ];

  const fetchProsecutorAppealData = () => {
    setLoading(true);

    const selectedStatus = STATUS_TABS.find((s) => s.label === activeTab)
      ?.status;
    const params: AppealListParams = {
      status: selectedStatus || undefined,
      search: debouncedSearch || undefined,
      page,
      pageSize: PAGE_SIZE,
      // This is the Cassation list — server returns only cassations.
      is_cassation: true,
    };

    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? fetchAppealForCoordinatorWithType(
            loginData?.admin_unit + "",
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
            params,
          )
        : isProsecutor
          ? fetchAppealForRequestingProsecutor(loginData?.user_id + "", params)
          : isRegistrar
            ? fetchAppealForRegistrar(loginData?.admin_unit + "", params)
            : selectedAdminRole
              ? fetchAppealForCoordinatorWithType(
                  loginData?.admin_unit + "",
                  selectedAdminRole,
                  params,
                )
              : isEconomicClerk
                ? fetchAppealForClerk(
                    loginData?.admin_unit + "",
                    Roles.economicClerk,
                    params,
                  )
                : isCorruptionClerk
                  ? fetchAppealForClerk(
                      loginData?.admin_unit + "",
                      Roles.corruptionClerk,
                      params,
                    )
                  : isVariousClerk
                    ? fetchAppealForClerk(
                        loginData?.admin_unit + "",
                        Roles.clerk,
                        params,
                      )
                    : fetchAppealRequest(params);

    api
      .then((res) => {
        const serverPaginated =
          res.next != null ||
          res.previous != null ||
          res.count > res.results.length;

        const cassations = (res.results || []).filter(
          (a) => (a as any)?.is_cassation,
        );

        if (serverPaginated) {
          setProsecutorAppeals(cassations);
          setCount(res.count);
        } else {
          // Fallback: filter + paginate client-side.
          let rows = cassations;
          if (selectedStatus) {
            rows = rows.filter((a) => a.status === selectedStatus);
          }
          const term = debouncedSearch.trim().toLowerCase();
          if (term) {
            rows = rows.filter((a) =>
              [
                a?.incident_registration_number,
                a?.crime_registration_number,
                a?.court_registration_number,
                a?.status,
                a?.appeal_type,
              ]
                .map((v) => (v ?? "").toString().toLowerCase())
                .some((v) => v.includes(term)),
            );
          }
          setCount(rows.length);
          setProsecutorAppeals(
            rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
          );
        }
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setProsecutorAppeals([]);
        setCount(0);
        setLoading(false);
      });
  };

  // Debounce the search box; reset to the first page on a new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (loginData && activeTab) {
      fetchProsecutorAppealData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loginData, page, debouncedSearch]);

  const totalPages = Math.ceil(count / PAGE_SIZE);
  const filtered = prosecutorAppeals;

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("cassation")}
        icon={FaBriefcase}
        tabs={STATUS_TABS.map((s) => s.label)}
        activeTab={activeTab}
        onTabClick={(tab) => {
          setActiveTab(tab);
          setPage(1);
        }}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_cassation")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("cassation_list_table")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">
                    {activeTab === "Prosecutor Appeal"
                      ? t("type")
                      : t("court_type")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === "Prosecutor Appeal"
                      ? t("crime_reference_no")
                      : t("court_appeal_number")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === "Prosecutor Appeal"
                      ? t("incident_registration_number")
                      : t("court_zone")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === "Prosecutor Appeal"
                      ? t("court_registration_number")
                      : t("court_woreda")}
                  </th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((appeal, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {"appeal_type" in appeal &&
                        appeal?.appeal_type &&
                        AppealRequestType[appeal.appeal_type]}{" "}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {
                        (appeal as AppealRequest)?.crime_registration_number
                      }{" "}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {(appeal as AppealRequest)?.incident_registration_number}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {(appeal as AppealRequest)?.court_registration_number}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge status={appeal?.status || ""} />
                    </td>

                    <td className="p-3 font-[500] font-[Poppins] flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigate(`/cassation-appeal/${appeal?.id}`);
                        }}
                        className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        {t("view")}
                      </button>
                      {(appeal as any)?.parent_appeal ||
                      (appeal as any)?.defendant_appeal_request_id ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-yellow-100 text-yellow-800 border border-yellow-300"
                          title={t("has_parent")}
                          aria-label={t("has_parent")}
                        >
                          ★
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_cassation_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {!loading && count > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {t("showing_page", {
                  defaultValue: `Page ${page} of ${Math.max(totalPages, 1)} · ${count} total`,
                  page,
                  total: Math.max(totalPages, 1),
                  count,
                })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ◀ {t("previous", { defaultValue: "Previous" })}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("next", { defaultValue: "Next" })} ▶
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CassationAppealList;
